import type {FormatXMLElementFn} from '@sail/intl';
import {message, useIntl} from '@sail/intl';
import {Text, Overlay, Link} from 'src/v2/components/common';
import {WalletIcon} from 'src/v2/components/wallet/WalletIcon';
import {useSelector, setValue} from 'src/v2/state/store';
import type {WalletOption} from 'src/v2/state/types';
import {safeWindowOpen} from '@stripe-internal/safe-links';

const messages = {
  continueTransaction: message<{
    walletProvider: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.wallet_connection_pending.continue_transaction',
    description: 'Prompt to tell the user to connect their wallet',
    defaultMessage: 'Confirm connection to {walletProvider}',
  }),
  iosPrompt: message<{
    walletProvider: React.ReactNode;
    Link: FormatXMLElementFn;
  }>({
    id: 'crypto.payins.v2.wallet_connection_pending.ios_prompt',
    description: 'Prompt to tell the user to install the wallet app on iOS',
    defaultMessage:
      'If the app doesn’t open, please download the {walletProvider} app from <Link>App Store</Link>.',
  }),
  androidPrompt: message<{
    walletProvider: React.ReactNode;
    Link: FormatXMLElementFn;
  }>({
    id: 'crypto.payins.v2.wallet_connection_pending.android_prompt',
    description: 'Prompt to tell the user to install the wallet app on Android',
    defaultMessage:
      'If the app doesn’t open, please download the {walletProvider} app from <Link>Google Play</Link>.',
  }),
  cancel: message({
    id: 'crypto.payins.v2.wallet_connection_pending.cancel',
    description: 'Prompt to tell the user to cancel the connection',
    defaultMessage: 'Cancel',
  }),
};

const MobileWalletConnectPrompt = ({
  walletOption,
}: {
  walletOption: WalletOption;
}) => {
  const intl = useIntl();
  const device = useSelector((state) => state.walletConnection.device);

  if (device === 'desktop') {
    return null;
  }

  const {downloadLinks, name} = walletOption;

  const deviceConfig = {
    ios: {
      message: messages.iosPrompt,
      url: `https://apps.apple.com/app/${downloadLinks.iosId}`,
    },
    android: {
      message: messages.androidPrompt,
      url: `https://play.google.com/store/apps/details?id=${downloadLinks.androidId}`,
    },
  };

  const currentDeviceConfig = deviceConfig[device as keyof typeof deviceConfig];

  return (
    <>
      <Text type="body">
        {currentDeviceConfig
          ? intl.formatMessage(currentDeviceConfig.message, {
              walletProvider: name,
              Link: (msg: string) => (
                <Link
                  type="secondary"
                  onClick={() => {
                    safeWindowOpen(currentDeviceConfig.url);
                  }}
                >
                  {msg}
                </Link>
              ),
            })
          : null}
      </Text>
      <Link
        type="secondary"
        onPress={() => {
          setValue((state) => {
            state.overlay = null;
          });
        }}
      >
        {intl.formatMessage(messages.cancel)}
      </Link>
    </>
  );
};

export const WalletConnectionPendingOverlay = () => {
  const intl = useIntl();
  const newWallet = useSelector((state) => state.newWallet);

  if (!newWallet) {
    throw new Error('newWallet is required in WalletConnectionPendingOverlay');
  }

  return (
    <Overlay name="wallet-connection-pending">
      <WalletIcon walletOption={newWallet.walletOption} />
      <Text type="heading">
        {intl.formatMessage(messages.continueTransaction, {
          walletProvider: newWallet.walletOption.name,
        })}
      </Text>
      <MobileWalletConnectPrompt walletOption={newWallet.walletOption} />
    </Overlay>
  );
};
